import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { updateSupplierUserStatus, getSupplierDocuments } from '../../../../models/suppliersModel';
import { API_BASE_URL } from '../../../../config';

const TABS = [
  { id: 'details', label: 'Supplier Details' },
  { id: 'users', label: 'Supplier Users' },
  { id: 'documents', label: 'Documents' }
];

const ViewSupplierModal = ({ supplier, onClose, loading = false, error = null, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);

  useEffect(() => {
    setActiveTab('details');
  }, [supplier?.BP_Code]);

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose(e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose(e);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const StatusBadge = ({ status }) => (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
        Number(status) === 1
          ? 'bg-green-100 text-green-800'
          : 'bg-red-100 text-red-800'
      }`}
    >
      {Number(status) === 1 ? 'Active' : 'Inactive'}
    </span>
  );

  const supplierUsers = supplier?.users ?? [];
  const rawSummary = supplier?.users_summary;

  const usersSummary = useMemo(() => {
    if (rawSummary) {
      return {
        total: rawSummary.total ?? supplierUsers.length,
        active:
          rawSummary.active ??
          supplierUsers.filter(
            (user) => Number(user.supplier_user_status) === 1
          ).length,
        view_only:
          rawSummary.view_only ??
          supplierUsers.filter(
            (user) => Number(user.supplier_user_view_only) === 1
          ).length
      };
    }

    const total = supplierUsers.length;
    const active = supplierUsers.filter(
      (user) => Number(user.supplier_user_status) === 1
    ).length;
    const viewOnly = supplierUsers.filter(
      (user) => Number(user.supplier_user_view_only) === 1
    ).length;

    return { total, active, view_only: viewOnly };
  }, [rawSummary, supplierUsers]);

  const handleToggleUserStatus = async (user) => {
    const newStatus = user.supplier_user_status === 1 ? 0 : 1;
    setUpdatingUserId(user.supplier_user_id);

    try {
      const response = await updateSupplierUserStatus(
        supplier.BP_Code,
        user.supplier_user_id,
        newStatus
      );

      if (response.status === 'success') {
        toast.success(`User ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        toast.error(response.message || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(error.response?.data?.message || 'Failed to update user status');
    } finally {
      setUpdatingUserId(null);
    }
  };

  // Fetch documents when Documents tab is activated
  useEffect(() => {
    if (activeTab === 'documents' && supplier?.BP_Code && documents.length === 0) {
      fetchDocuments();
    }
  }, [activeTab, supplier?.BP_Code]);

  const fetchDocuments = async () => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    
    try {
      const response = await getSupplierDocuments(supplier.BP_Code);
      if (response.status === 'success') {
        setDocuments(response.data || []);
      } else {
        setDocumentsError(response.message || 'Failed to load documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocumentsError(error.response?.data?.message || 'Failed to load documents');
    } finally {
      setDocumentsLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const handleDownloadDocument = (doc) => {
    // Check if it's a legacy file (has source='legacy') or new attachment
    if (doc.source === 'legacy') {
      // Legacy files use the storage controller
      const downloadUrl = `${API_BASE_URL}/files/storage?file=${encodeURIComponent(doc.file_path)}`;
      window.open(downloadUrl, '_blank');
    } else {
      // New attachments use the suppliers attachment endpoint
      const downloadUrl = `${API_BASE_URL}/api/attachments/suppliers/${doc.file_path}`;
      window.open(downloadUrl, '_blank');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Supplier Details</h3>
            <p className="text-sm text-gray-500 mt-1">{supplier?.BP_Name}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-4" aria-label="Supplier detail tabs">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 py-2 border-b-2 text-sm font-medium focus:outline-none ${
                      isActive
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="mt-6 space-y-6">
            {activeTab === 'details' && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Basic Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">BP Code</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {supplier?.BP_Code || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Vendor ID</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {supplier?.VendorID || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Company Name</label>
                        <p className="mt-1 text-sm text-gray-900 font-medium">
                          {supplier?.BP_Name || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Group</label>
                        <div className="mt-1">
                          <p className="text-sm text-gray-900">
                            {supplier?.Group_Code || 'Not set'}
                          </p>
                          {supplier?.Supplier_Group_Name && (
                            <p className="text-xs text-gray-500">
                              {supplier.Supplier_Group_Name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Company Registration
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {supplier?.Company_Reg || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <div className="mt-1">
                          <StatusBadge status={supplier?.Status} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Contact Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Contact Person
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {supplier?.Contact_Person || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <div className="mt-1 text-sm text-gray-900">
                          {supplier?.E_Mail ? (
                            <div className="break-words">
                              {supplier.E_Mail.split(',').map((email, index, arr) => (
                                <span key={index}>
                                  <a
                                    href={`mailto:${email.trim()}`}
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    {email.trim()}
                                  </a>
                                  {index < arr.length - 1 && ', '}
                                </span>
                              ))}
                            </div>
                          ) : (
                            'Not set'
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Mobile Phone
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {supplier?.Mobile_Phone ? (
                            <a
                              href={`tel:${supplier.Mobile_Phone}`}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              {supplier.Mobile_Phone}
                            </a>
                          ) : (
                            'Not set'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">BBBEE Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          BBBEE Type
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {supplier?.BBBEE_Type || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          BBBEE Level
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {supplier?.BBBEE_Level || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          BBBEE Expiry Date
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {formatDate(supplier?.BBBEE_Expiration_Date)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Compliance Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          COIDA Certificate Number
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {supplier?.COIDA_Certificate_Number || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          COIDA Expiry Date
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {formatDate(supplier?.COIDA_Certificate_Expiry_Date)}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Tax Certificate Number
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {supplier?.Tax_Certification_Number || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Tax Certificate Expiry Date
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {formatDate(supplier?.Tax_Certificate_Expiry_Date)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Compliance Status</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-sm font-medium text-gray-700">BBBEE Status</div>
                        <div className="mt-1">
                          {supplier?.BBBEE_Expiration_Date ? (
                            new Date(supplier.BBBEE_Expiration_Date) > new Date() ? (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Valid
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                Expired
                              </span>
                            )
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                              Not Set
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-center">
                        <div className="text-sm font-medium text-gray-700">COIDA Status</div>
                        <div className="mt-1">
                          {supplier?.COIDA_Certificate_Expiry_Date ? (
                            new Date(supplier.COIDA_Certificate_Expiry_Date) > new Date() ? (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Valid
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                Expired
                              </span>
                            )
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                              Not Set
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-center">
                        <div className="text-sm font-medium text-gray-700">Tax Status</div>
                        <div className="mt-1">
                          {supplier?.Tax_Certificate_Expiry_Date ? (
                            new Date(supplier.Tax_Certificate_Expiry_Date) > new Date() ? (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Valid
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                Expired
                              </span>
                            )
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                              Not Set
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900">Supplier Users</h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Total Users
                    </p>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {usersSummary.total ?? 0}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Active
                    </p>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {usersSummary.active ?? 0}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      View Only
                    </p>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {usersSummary.view_only ?? 0}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <svg
                          className="animate-spin h-4 w-4 text-blue-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Loading supplier users...</span>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                      {error}
                    </div>
                  ) : supplierUsers.length === 0 ? (
                    <div className="rounded-md border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
                      No supplier users found for this company.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              User
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Position
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Access
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {supplierUsers.map((user, index) => {
                            const key =
                              user.supplier_user_id ??
                              `${user.supplier_user_email ?? 'user'}-${index}`;
                            const isActive = Number(user.supplier_user_status) === 1;
                            const isViewOnly = Number(user.supplier_user_view_only) === 1;
                            const isAdmin = Number(user.supplier_user_admin) === 1;

                            return (
                              <tr key={key} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3">
                                  <div className="font-medium text-gray-900">
                                    {user.supplier_user_name || 'Not provided'}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {user.supplier_user_email || '—'}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {user.supplier_user_position || 'Not set'}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap gap-2">
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                        isViewOnly
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : 'bg-blue-100 text-blue-800'
                                      }`}
                                    >
                                      {isViewOnly ? 'View Only' : 'Full Access'}
                                    </span>
                                    {isAdmin && (
                                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                        Admin
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-center">
                                    <button
                                      type="button"
                                      onClick={() => handleToggleUserStatus(user)}
                                      disabled={updatingUserId === user.supplier_user_id}
                                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                                        isActive
                                          ? 'bg-green-600'
                                          : 'bg-gray-200'
                                      } ${
                                        updatingUserId === user.supplier_user_id
                                          ? 'opacity-50 cursor-not-allowed'
                                          : 'cursor-pointer'
                                      }`}
                                      title={isActive ? 'Click to deactivate' : 'Click to activate'}
                                    >
                                      <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                          isActive ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                      />
                                    </button>
                                    {updatingUserId === user.supplier_user_id && (
                                      <svg
                                        className="animate-spin ml-2 h-4 w-4 text-indigo-600"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"
                                        ></circle>
                                        <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                      </svg>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900">Supplier Documents</h4>

                <div className="bg-gray-50 rounded-lg p-4">
                  {documentsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <svg
                          className="animate-spin h-4 w-4 text-blue-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Loading documents...</span>
                      </div>
                    </div>
                  ) : documentsError ? (
                    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                      {documentsError}
                    </div>
                  ) : documents.length === 0 ? (
                    <div className="rounded-md border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
                      No documents found for this supplier.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Document Name
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {documents.map((doc, index) => (
                            <tr key={doc.attachment_id || index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  <span className="text-sm font-medium text-gray-900">
                                    {doc.file_name || 'Unnamed Document'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() => handleDownloadDocument(doc)}
                                  className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                >
                                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Download
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewSupplierModal;
